import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import { Alert, Collapse, Stack, TextField } from "@mui/material"
import Grid from "@mui/material/Unstable_Grid2"
import { ReactElement, SyntheticEvent, useState } from "react"
import { useSearchParams } from "react-router-dom"

import AppLoadingButton from "../../components/buttons/appLoadingButton"
import DataLoadError from "../../components/fallback/dataLoadError"
import DraftsFilterPanelFrame from "../../components/fitlerPanel/draftsFilterPanelFrame"
import AppControlledDateRange from "../../components/inputs/controlled/appControlledDateRange"
import AppControlledSelect from "../../components/inputs/controlled/appControlledSelect"
import { COMPLETION_STATUS } from "../../components/submitApplication/single/draftCompletionStatus/draftCompletionStatus"
import { LCT_TILES } from "../../components/submitApplication/single/selectLct/applicationTypeTiles"
import AppPaper from "../../components/surfaces/appPaper"
import InstallerDraftTable from "../../components/tables/draftTable/installerDraftTable"
import {
    DRAFT_ID,
    DRAFT_UPDATED_DATE_FROM,
    DRAFT_UPDATED_DATE_TO,
    POSTCODE,
    SLIDE_IN_TIMEOUT,
    STATUS_FROM,
    STATUS_TO,
    TECHNOLOGY,
} from "../../constants/generalConstants"
import { DRAFT_INFO_BANNER } from "../../constants/messages"
import { TT_HELP_INSTALLER_DRAFT_APPLICATIONS } from "../../constants/tooltips"
import { useDraftsApplicationFilters } from "../../hooks/useDraftsApplicationFilters"
import { useGetState } from "../../hooks/useGetState"
import { getShowSearchFilters } from "../../redux/preferencesSlice"
import useGetInstallerDrafts from "../../services/middleware/hooks/installerInterface/useGetInstallerDrafts"
import { GRID_SPACING } from "../../themes/defaultThemeParams"

/**
 * InstallerListDrafts is the main component for the Installer Drafts page.
 * It displays the list of drafts that the installer has created.
 * It also provides filtering options to filter the drafts.
 */
const InstallerListDrafts = (): ReactElement => {
    const [sortKey, setSortKey] = useState<string>("")
    const [searchParams, setSearchParams] = useSearchParams()
    const {
        data,
        isError,
        isValidating,
        isLoading,
        isFinalPage,
        page,
        setPage,
        mutate,
    } = useGetInstallerDrafts(searchParams, sortKey)

    const showFilters = useGetState(getShowSearchFilters)

    // Filter and sort hooks
    const {
        draftId,
        setFilterFromTextfield,
        orderBy,
        orderDirection,
        dateFrom,
        dateTo,
        status,
        setOrderBy,
        technology,
        postcode,
    } = useDraftsApplicationFilters(searchParams, setSearchParams)
    // Local states only to be used sparingly - where possible rely on URL
    // params - this is to reduce renders
    const [localDraftId, setLocalDraftId] = useState<string>(draftId)
    const [localPostcode, setLocalPostcode] = useState<string>(postcode || "")

    // Technology mapping from LCT_TILES for filtering
    const technologyMapping = LCT_TILES.map((tile) => ({
        label: tile.title,
        value: tile.value,
    }))
    const techOptions: string[] = LCT_TILES.map((tile) => tile.title)

    // Completion status mapping from COMPLETION_STATUS for filtering
    const completionStatusMapping = COMPLETION_STATUS.map(
        (completionStatus) => ({
            label: completionStatus.title,
            statusFrom: completionStatus.statusFrom,
            statusTo: completionStatus.statusTo,
        })
    )
    const statusOptions: string[] = COMPLETION_STATUS.map(
        (completionStatus) => completionStatus.title
    )

    const handleSortChange = (newSortKey: string): void => {
        setSortKey(newSortKey)
    }

    return (
        <Grid container spacing={GRID_SPACING}>
            <Grid component="div" alignSelf="left">
                <Alert severity="info">{DRAFT_INFO_BANNER}</Alert>
            </Grid>
            <DraftsFilterPanelFrame
                draftId={draftId}
                isValidating={isValidating}
                drafts="My Drafts"
                tooltip={TT_HELP_INSTALLER_DRAFT_APPLICATIONS}
                clearLocalDraftId={(): void => setLocalDraftId("")}
                clearLocalPostcode={(): void => setLocalPostcode("")}
            />
            <Grid xs={12}>
                <AppPaper>
                    <Collapse in={showFilters} timeout={SLIDE_IN_TIMEOUT}>
                        <Grid
                            container
                            spacing={GRID_SPACING + 1}
                            marginBottom={4}
                            marginTop={1}
                            columns={12}
                            display="flex"
                            alignItems="center"
                        >
                            <Grid xl={3} sm={6} xs={12}>
                                <TextField
                                    label="Draft ID"
                                    value={localDraftId}
                                    placeholder="Full Draft ID required..."
                                    onChange={(event) => {
                                        setLocalDraftId(event.target.value)
                                        setFilterFromTextfield(
                                            event.target.value,
                                            DRAFT_ID
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid xl={3} sm={6} xs={12}>
                                <AppControlledDateRange
                                    label="Date from"
                                    dateValue={dateFrom}
                                    setDateValue={(value: string) =>
                                        setFilterFromTextfield(
                                            value,
                                            DRAFT_UPDATED_DATE_FROM
                                        )
                                    }
                                />
                            </Grid>
                            <Grid xl={3} sm={6} xs={12}>
                                <AppControlledDateRange
                                    label="Date to"
                                    dateValue={dateTo}
                                    setDateValue={(value: string) =>
                                        setFilterFromTextfield(
                                            value,
                                            DRAFT_UPDATED_DATE_TO
                                        )
                                    }
                                />
                            </Grid>
                            <Grid xl={3} sm={6} xs={12}>
                                <TextField
                                    label="Postcode"
                                    value={localPostcode}
                                    placeholder="Enter postcode..."
                                    onChange={(event) => {
                                        setLocalPostcode(event.target.value)
                                        setFilterFromTextfield(
                                            event.target.value,
                                            POSTCODE
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid xl={3} sm={6} xs={12}>
                                <AppControlledSelect
                                    value={status}
                                    options={statusOptions}
                                    label="Completion Status"
                                    setValue={(
                                        _event: SyntheticEvent<Element, Event>,
                                        newValue: string | number | null
                                    ) => {
                                        const selectedStatus =
                                            completionStatusMapping.find(
                                                (item) =>
                                                    item.label === newValue
                                            )

                                        // to filter your drafts by the tile's value
                                        setFilterFromTextfield(
                                            selectedStatus?.statusFrom || null,
                                            STATUS_FROM
                                        )
                                        setFilterFromTextfield(
                                            selectedStatus?.statusTo || null,
                                            STATUS_TO
                                        )
                                    }}
                                    loading={false}
                                    fullWidth
                                />
                            </Grid>
                            <Grid xl={3} sm={6} xs={12}>
                                <AppControlledSelect
                                    value={technology}
                                    options={techOptions}
                                    label="Technology"
                                    setValue={(
                                        _event: SyntheticEvent<Element, Event>,
                                        newValue: string | number | null
                                    ) => {
                                        const selectedTile =
                                            technologyMapping.find(
                                                (item) =>
                                                    item.label === newValue
                                            )

                                        // to filter your drafts by the tile's value
                                        setFilterFromTextfield(
                                            selectedTile?.value || "",
                                            TECHNOLOGY
                                        )
                                    }}
                                    loading={false}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </Collapse>
                    {isError ? (
                        <DataLoadError />
                    ) : (
                        <Stack spacing={GRID_SPACING}>
                            <InstallerDraftTable
                                searchResults={data}
                                orderBy={orderBy}
                                setOrderBy={setOrderBy}
                                orderDirection={orderDirection}
                                isLoading={isLoading}
                                onSortChange={handleSortChange}
                                mutate={mutate}
                            />
                            {data && (
                                <Stack
                                    alignItems="center"
                                    justifyContent="center"
                                    direction="row"
                                    width="100%"
                                >
                                    {!isFinalPage && (
                                        <AppLoadingButton
                                            color="secondary"
                                            endIcon={<MoreHorizIcon />}
                                            loading={isValidating}
                                            loadingPosition="end"
                                            onClick={() => setPage(page + 1)}
                                            sx={{
                                                width: "40%",
                                                mt: "6px",
                                            }}
                                        >
                                            See More
                                        </AppLoadingButton>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                        // )
                    )}
                </AppPaper>
            </Grid>
        </Grid>
    )
}

export default InstallerListDrafts
